import type { CSSProperties } from "react";
import type { ViewValue } from "./types";
import { getViewValueStyle } from "./views";

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parses `#rgb` / `#rrggbb`; returns `null` for anything else. */
function parseHexColor(color: string): Rgb | null {
  const hex = color.trim().replace(/^#/, "");
  const full = hex.length === 3 ? [...hex].map((ch) => ch + ch).join("") : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  const value = Number.parseInt(full, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

/** WCAG relative luminance (0 = black, 1 = white). */
function getRelativeLuminance(color: string): number {
  const rgb = parseHexColor(color);
  if (!rgb) return 0;
  const linear = (channel: number) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(rgb.r) + 0.7152 * linear(rgb.g) + 0.0722 * linear(rgb.b);
}

/** Dark text on light backgrounds, white text on dark ones. */
function getAdaptiveTextColor(background: string): string {
  return getRelativeLuminance(background) > 0.5 ? "#1f2d33" : "#ffffff";
}

/** Style for an intervention box in the matrix. */
export function getMatrixItemStyle(value: ViewValue | undefined): CSSProperties {
  if (!value) return {};
  return {
    color: getAdaptiveTextColor(value.color),
    borderColor: "#ffffff",
    ...getViewValueStyle(value),
  };
}
